/**
 * Organization Profile (Sprint C14 Lane B WP-8)
 *
 * FI-tenant operator view of the institution's profile. Of the canonical
 * UpdateTenantRequest fields the page edits three:
 *   - name           (institution display name)
 *   - charterNumber  (institution charter)
 *   - type           (CREDIT_UNION | COMMUNITY_BANK | etc.)
 *
 * GH #673 (operator ruling 2026-10-23): primaryContact, the tenant's primary
 * email address, is the one field exempt from self-service. The page shows it
 * read-only with Request change, which opens a tracked support case, and the
 * PATCH route refuses it from the FI side. Editing needs FI_ADMIN (or a
 * platform-admin role), the role the route checks; any other session reads
 * the profile without an edit control, charter number and type among the
 * onboarding details. A save that changes the charter number or the type
 * first asks the operator to verify (step-up, operation updateTenantCharter,
 * subject the tenant id; ruling Q3c), and sends the minted token as
 * X-Step-Up-Token.
 *
 * Read-only fields surfaced for context (sourced from the institution
 * record at onboarding), each changed through Request change:
 *   - status          (ACTIVE | PENDING | SUSPENDED | INACTIVE)
 *   - purpose         (CUSTOMER | PILOT | DEMO | PROBE; GH #4096, set once at
 *                      provisioning under the operator ruling of 2026-10-22)
 *   - palEnabled      (Pledged-Asset Lending feature flag)
 *
 * Backend: platform-admin-api routes
 *   GET   /api/admin/tenants/{tenantId}
 *   PATCH /api/admin/tenants/{tenantId}
 *
 * The tenantId is resolved from the auth store -- the FI operator is
 * always editing their own tenant. Cross-tenant editing for platform
 * admins lives at /admin/tenants/{id} (TenantManagementPage).
 *
 * GH #1939. A failed first read renders a notice in place of the form. The
 * page used to draw the editable fields blank under a one-line banner, after
 * re-issuing a refused read three more times.
 *
 * A 403 here means the page asked for a tenant other than the one the session
 * resolves to: tenant-admin.routes.ts GET /:id refuses only a cross-tenant read
 * by a caller who is not a platform admin, and an impersonation session
 * resolves to the impersonated tenant (tenant-resolver.ts step 1; the
 * 2026-07-24 prod walkthrough read GET /tenants/f2a4 200 under the down-scoped
 * fi_admin cookie, sprint-auth-substrate-hardening esc-3). The page cannot tell
 * why the two differ (the apex fallback below, or a stale store record), so the
 * notice is one neutral sentence for every persona plus a support case naming
 * both tenants. The 2026-10-22 ruling on #1939 covers the loan-syndication
 * NO_PARTICIPANT_FOR_TENANT 403, which this route never returns.
 */
import { Link } from 'react-router-dom';
import { requestChangeHref } from '../../utils/request-change';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, RotateCcw, Save } from 'lucide-react';
import { Alert, Button, Heading, Input, Select, Skeleton } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { platformAdminClient } from '../../api/client';
import { apiErrorMessage } from '../../api/error-message';
import { useAuthStore } from '../../stores/auth';
import { hasPlatformAdminRole } from '../../lib/platform-admin-roles';
import { StepUpAuthModal } from '../../components/staff/StepUpAuthModal';

type TenantDetail = components['schemas']['TenantDetail'];
type UpdateTenantRequest = components['schemas']['UpdateTenantRequest'];
type InstitutionType = components['schemas']['InstitutionType'];

/**
 * Apex tenant id ("Aaim, Inc."). Mirrors APEX_TENANT_ID from
 * @aaimplatform/database/security/tenant-resolver per
 * .claude/rules/multi-tenant-request-shape.md. An apex platform admin
 * authenticates without a tenantId claim; resolveEffectiveTenantId
 * (server-side) resolves that to APEX_TENANT_ID. SPA-side surfaces that
 * read user.tenantId raw must do the same fallback so apex Aaim Inc.
 * is shaped exactly like any other FI tenant for self-administration
 * surfaces. Tracked as F-SPA-TENANT-RESOLVER-CENTRALIZATION for a
 * shared hook that replaces the inline mirror across consumers.
 */
const APEX_TENANT_ID = '00000000-0000-0000-0000-000000aaaaaa';

const QUERY_KEY_BASE = ['admin', 'tenants', 'me', 'profile'] as const;
const LOAD_FAILED = 'Failed to load organization profile';
const REFUSED_HEADLINE = 'Organization profile not available to this session';
/** Shown when no response arrived, in place of the browser's own fetch error. */
const UNREACHABLE = 'The platform could not be reached. Check the connection and retry.';
const NAME_MAX = 255;
const STEP_UP_REFUSED =
  'Your verification was not accepted or has expired. Save again to verify your identity.';

/**
 * Every InstitutionType with its label, in display order. Keyed by the
 * generated union, so a type added to platform-admin.openapi.yaml fails tsc
 * here until it is listed.
 */
const INSTITUTION_TYPE_LABELS: Record<InstitutionType, string> = {
  CREDIT_UNION: 'Credit Union',
  COMMUNITY_BANK: 'Community Bank',
  REGIONAL_BANK: 'Regional Bank',
  FINTECH: 'Fintech',
  BROKER_DEALER: 'Broker-Dealer',
  INSURANCE: 'Insurance',
  TRANSFER_AGENT: 'Transfer Agent',
  TRUST_COMPANY: 'Trust Company',
  OTHER: 'Other',
};

const INSTITUTION_TYPE_OPTIONS = (Object.keys(INSTITUTION_TYPE_LABELS) as InstitutionType[]).map((value) => ({
  value,
  label: INSTITUTION_TYPE_LABELS[value],
}));

/**
 * The role the PATCH route admits on the FI side (GH #673), compared
 * case-insensitively. A platform-admin role also edits.
 */
const PROFILE_EDITOR_ROLE = 'fi_admin';

function canEditProfile(roles: readonly unknown[] | null | undefined): boolean {
  return (
    hasPlatformAdminRole(roles) ||
    (roles ?? []).some((role) => typeof role === 'string' && role.toLowerCase() === PROFILE_EDITOR_ROLE)
  );
}

interface FormState {
  name: string;
  charterNumber: string;
  type: InstitutionType;
}

function formFromTenant(tenant: TenantDetail): FormState {
  return { name: tenant.name, charterNumber: tenant.charterNumber ?? '', type: tenant.type };
}

/** The HTTP status the profile query function rides on its Error, if any. */
function readStatus(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
}

function buildUpdate(form: FormState, baseline: TenantDetail | undefined): UpdateTenantRequest {
  if (!baseline) return {};
  const baselineForm = formFromTenant(baseline);
  const update: UpdateTenantRequest = {};
  if (form.name !== baselineForm.name) update.name = form.name;
  if (form.charterNumber !== baselineForm.charterNumber) update.charterNumber = form.charterNumber;
  if (form.type !== baselineForm.type) update.type = form.type;
  return update;
}

/** A charter number or type edit is the updateTenantCharter operation. */
function needsStepUp(update: UpdateTenantRequest): boolean {
  return update.charterNumber !== undefined || update.type !== undefined;
}

/** The error code the PATCH answered with, when it sent one. */
function errorCode(error: unknown): string | undefined {
  const code = (error as { error?: unknown } | null)?.error;
  return typeof code === 'string' ? code : undefined;
}

export function OrganizationProfilePage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  // Resolve effective tenantId: apex platform admins have no tenantId
  // claim and resolve to APEX_TENANT_ID per .claude/rules/multi-tenant-
  // request-shape.md. Apex Aaim Inc. self-administers as any other FI.
  const tenantId = user ? user.tenantId ?? APEX_TENANT_ID : undefined;
  const canEdit = canEditProfile(user?.roles);

  const [form, setForm] = useState<FormState>({ name: '', charterNumber: '', type: 'OTHER' });
  const [nameError, setNameError] = useState<string | null>(null);
  const [charterError, setCharterError] = useState<string | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [stepUpOpen, setStepUpOpen] = useState(false);

  const queryKey = useMemo(() => [...QUERY_KEY_BASE, tenantId] as const, [tenantId]);

  const tenantQuery = useQuery({
    queryKey,
    enabled: typeof tenantId === 'string' && tenantId.length > 0,
    queryFn: async (): Promise<TenantDetail> => {
      if (!tenantId) throw new Error('Tenant context required');
      const result = await platformAdminClient.GET('/tenants/{tenantId}', {
        params: { path: { tenantId } },
      });
      // The status rides on the Error so main.tsx's retry predicate
      // (src/api/query-retry.ts) stops at a 401/403, and so the page can tell a
      // refusal from a failure. A bare Error was retried 3x on a 403 (#1939).
      const status = result.response.status;
      const { data, error } = result;
      if (error || !data) {
        throw Object.assign(new Error(apiErrorMessage(error) ?? LOAD_FAILED), { status });
      }
      return data;
    },
    staleTime: 30_000,
  });

  useEffect(() => {
    if (tenantQuery.data) {
      setForm(formFromTenant(tenantQuery.data));
    }
  }, [tenantQuery.data]);

  const isDirty = useMemo(() => {
    if (!tenantQuery.data) return false;
    const update = buildUpdate(form, tenantQuery.data);
    return Object.keys(update).length > 0;
  }, [form, tenantQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (stepUpToken: string | undefined) => {
      if (!tenantId) throw new Error('Tenant context required');
      const body = buildUpdate(form, tenantQuery.data);
      const { data, error } = await platformAdminClient.PATCH('/tenants/{tenantId}', {
        params: {
          path: { tenantId },
          ...(stepUpToken ? { header: { 'X-Step-Up-Token': stepUpToken } } : {}),
        },
        body,
      });
      if (error || !data) {
        const code = errorCode(error);
        throw new Error(
          code === 'STEP_UP_REQUIRED' || code === 'STEP_UP_EXPIRED'
            ? STEP_UP_REFUSED
            : 'Failed to save organization profile',
        );
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setSaveErrorMessage(null);
    },
    onError: (error: Error) => {
      setSaveErrorMessage(error.message);
    },
  });

  const handleNameChange = (value: string) => {
    setForm((prev) => ({ ...prev, name: value }));
    if (value.length === 0) {
      setNameError('Organization name is required');
    } else if (value.length > NAME_MAX) {
      setNameError(`Name must be at most ${NAME_MAX} characters`);
    } else {
      setNameError(null);
    }
  };

  const handleCharterNumberChange = (value: string) => {
    setForm((prev) => ({ ...prev, charterNumber: value }));
    // A recorded charter number may be corrected, not cleared.
    setCharterError(
      value.trim().length === 0 && (tenantQuery.data?.charterNumber ?? '') !== ''
        ? 'Charter number cannot be blank'
        : null,
    );
  };

  const handleReset = () => {
    if (tenantQuery.data) {
      setForm(formFromTenant(tenantQuery.data));
    }
    setNameError(null);
    setCharterError(null);
    setSaveErrorMessage(null);
  };

  // A charter number or type change is verified first; the token then rides
  // the PATCH. Any other edit saves at once.
  const handleSave = () => {
    setSaveErrorMessage(null);
    if (needsStepUp(buildUpdate(form, tenantQuery.data))) {
      setStepUpOpen(true);
      return;
    }
    saveMutation.mutate(undefined);
  };

  const canSave =
    canEdit && isDirty && !nameError && !charterError && !saveMutation.isPending && tenantQuery.data;

  if (!tenantId) {
    return (
      <div>
        <Heading level={2}>Organization Profile</Heading>
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mt-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Sign in to manage the organization profile.
          </div>
        </div>
      </div>
    );
  }

  if (tenantQuery.isLoading) {
    return (
      <div>
        <Heading level={2}>Organization Profile</Heading>
        <div className="mt-6">
          <Skeleton className="h-40 rounded-sm mb-6" />
          <Skeleton className="h-48 rounded-sm" />
        </div>
      </div>
    );
  }

  const tenant = tenantQuery.data;

  // A failed FIRST read has no profile to edit. Drawing the form anyway showed
  // blank name and contact fields beside a Save button, which read as an empty
  // record rather than an unread one.
  if (tenantQuery.isError && !tenant) {
    const loadStatus = readStatus(tenantQuery.error);
    const sessionTenant = user?.tenantId ?? 'none';
    const sessionKind = user?.impersonatedBy ? ' (impersonation session)' : '';
    // No status means no response arrived (network down, proxy reset), and the
    // Error then carries the browser's own fetch text; the page says it itself.
    const failureDetail =
      loadStatus === undefined
        ? UNREACHABLE
        : tenantQuery.error instanceof Error && tenantQuery.error.message
          ? tenantQuery.error.message
          : LOAD_FAILED;
    return (
      <div>
        <Heading level={2}>Organization Profile</Heading>
        <div role="alert" className="mt-6">
          {loadStatus === 403 ? (
            <Alert type="warning" title={REFUSED_HEADLINE}>
              <div className="space-y-2">
                <div className="text-xs">
                  The platform refused this session&apos;s read of the requested
                  institution&apos;s profile (HTTP 403). The profile fields are not shown,
                  so nothing here reads as a blank or editable value.
                </div>
                <Link
                  to={requestChangeHref({
                    surface: 'Organization profile',
                    item: 'Access to the organization profile',
                    detail: `The profile read was refused with HTTP 403. Requested institution ${tenantId}; session tenant ${sessionTenant}${sessionKind}.`,
                  })}
                  className="text-xs text-accent hover:underline"
                >
                  Open a support case
                </Link>
              </div>
            </Alert>
          ) : (
            <Alert type="error" title={LOAD_FAILED}>
              <div className="space-y-2">
                <div className="text-xs">
                  {failureDetail}
                  {loadStatus !== undefined ? ` (HTTP ${loadStatus})` : ''}
                </div>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => {
                    void tenantQuery.refetch();
                  }}
                >
                  Retry
                </Button>
              </div>
            </Alert>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6 sm:gap-6">
        <div>
          <Heading level={2}>Organization Profile</Heading>
          <div className="text-sm font-sans text-tertiary leading-normal mt-2 max-w-2xl">
            {canEdit
              ? 'Your institution\'s display name, charter number and institution type are yours to edit. The primary contact email and the other onboarding details change through Request change, which opens a tracked case.'
              : 'Your institution\'s profile. An FI administrator edits the display name, charter number and institution type; the primary contact email and the other onboarding details change through Request change, which opens a tracked case.'}
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
            <Button variant="secondary" onClick={handleReset} disabled={!isDirty || saveMutation.isPending}>
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!canSave}>
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      {(tenantQuery.error || saveErrorMessage) && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {tenantQuery.error ? LOAD_FAILED : saveErrorMessage}
          </div>
        </div>
      )}

      {/* Display name (editable by FI_ADMIN) and primary contact (read-only) */}
      <div className="bg-surface rounded-sm shadow-sm p-6 mb-6">
        <Heading level={3} className="mb-6">{canEdit ? 'Editable details' : 'Details'}</Heading>

        <div className="space-y-6">
          <div>
            {/* A label only where there is an input to label; the read-only
                name gets a plain caption, as the primary contact does. */}
            {canEdit ? (
              <label
                htmlFor="organization-name"
                className="text-sm font-sans font-medium text-primary leading-normal block mb-2"
              >
                Organization name
              </label>
            ) : (
              <div className="text-sm font-sans font-medium text-primary leading-normal mb-2">
                Organization name
              </div>
            )}
            {canEdit ? (
              <input
                id="organization-name"
                type="text"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                maxLength={NAME_MAX}
                className={`w-full px-3 py-2 border rounded-sm text-sm font-sans focus:outline-none focus:border-accent ${
                  nameError ? 'border-error' : 'border-recessed'
                }`}
              />
            ) : (
              <div className="text-sm font-sans text-primary leading-normal">
                {tenant?.name}
              </div>
            )}
            {nameError && (
              <div className="text-xs font-sans text-error leading-normal mt-2">{nameError}</div>
            )}
          </div>

          {canEdit && tenant && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Charter number"
                value={form.charterNumber}
                onChange={(e) => handleCharterNumberChange(e.target.value)}
                error={charterError ?? undefined}
                helperText="Your regulator-issued charter number. Changing it asks you to verify your identity."
              />
              <Select
                label="Institution type"
                options={INSTITUTION_TYPE_OPTIONS}
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value as InstitutionType }))}
                helperText="Changing it asks you to verify your identity."
              />
            </div>
          )}

          <div>
            <div className="text-sm font-sans font-medium text-primary leading-normal mb-2">
              Primary contact email
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-mono text-primary leading-normal">
                {tenant?.primaryContact || <span className="font-sans text-tertiary italic">Not set</span>}
              </span>
              <Link
                to={requestChangeHref({
                  surface: 'Organization profile',
                  item: 'Change to the primary contact email',
                  detail: tenant?.name ? `Institution: ${tenant.name}` : undefined,
                })}
                className="text-sm font-sans text-accent hover:underline"
              >
                Request change
              </Link>
            </div>
            <div className="text-xs font-sans text-tertiary leading-normal mt-1">
              Primary email of record for operational notifications and Aaim team
              correspondence. It is your institution&apos;s identity of record, so a
              change goes through a tracked case.
            </div>
          </div>
        </div>
      </div>

      {/* Read-only context */}
      {tenant && (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          <Heading level={3} className="mb-2">Onboarding details (read-only)</Heading>
          <div className="text-sm font-sans text-tertiary leading-normal mb-6 flex flex-wrap items-center gap-2">
            <span>
              These fields are established at onboarding and are re-verified rather
              than edited.
            </span>
            {/* GH #1962: was "contact your account manager" -- a dead end. */}
            <Link
              to={requestChangeHref({
                surface: 'Organization profile -- onboarding details',
                item: 'Change to a read-only onboarding field',
                detail: tenant?.name ? `Institution: ${tenant.name}` : undefined,
              })}
              className="text-accent hover:underline"
            >
              Request change
            </Link>
          </div>

          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* An editor changes these two above; everyone else reads them here. */}
            {!canEdit && (
              <div>
                <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                  Charter number
                </dt>
                <dd className="text-sm font-mono text-primary leading-normal">
                  {tenant.charterNumber || <span className="text-tertiary italic">Not set</span>}
                </dd>
              </div>
            )}
            {!canEdit && (
              <div>
                <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                  Institution type
                </dt>
                <dd className="text-sm font-sans text-primary leading-normal">{tenant.type}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                Status
              </dt>
              <dd>
                <span
                  className={`px-2 py-0.5 text-xs font-sans font-semibold rounded leading-normal ${
                    tenant.status === 'ACTIVE'
                      ? 'bg-success/10 text-success'
                      : tenant.status === 'SUSPENDED' || tenant.status === 'INACTIVE'
                        ? 'bg-error/10 text-error'
                        : 'bg-tertiary/10 text-tertiary'
                  }`}
                >
                  {tenant.status}
                </span>
              </dd>
            </div>
            <div>
              {/* Tenant.purpose is required on the record. It prints as the
                  code, as Institution type and Status beside it do. */}
              <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                Purpose
              </dt>
              <dd className="text-sm font-sans text-primary leading-normal">{tenant.purpose}</dd>
            </div>
            <div>
              <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                PAL enabled
              </dt>
              <dd className="text-sm font-sans text-primary leading-normal">
                {tenant.palEnabled ? 'Yes' : 'No'}
              </dd>
            </div>
            {tenant.state && (
              <div>
                <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                  State
                </dt>
                <dd className="text-sm font-sans text-primary leading-normal">{tenant.state}</dd>
              </div>
            )}
            {tenant.charter && (
              <div>
                <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                  Charter
                </dt>
                <dd className="text-sm font-sans text-primary leading-normal">{tenant.charter}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                Onboarded
              </dt>
              <dd className="text-sm font-sans text-primary leading-normal">
                {new Date(tenant.createdAt).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                Institution ID
              </dt>
              <dd className="text-xs font-mono text-secondary leading-normal break-all">
                {tenant.id}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {tenantId && (
        <StepUpAuthModal
          open={stepUpOpen}
          onOpenChange={setStepUpOpen}
          operation="updateTenantCharter"
          subjectUserId={tenantId}
          operationLabel={`Change the charter details of ${tenant?.name ?? 'this institution'}`}
          onVerified={(stepUpToken) => {
            setStepUpOpen(false);
            saveMutation.mutate(stepUpToken);
          }}
          onCancel={() => setStepUpOpen(false)}
        />
      )}
    </div>
  );
}
